/**
 * Item Import API — RTK Query slice for Excel extract and import.
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { client } from '../../../api/NestClient';
import { nestBaseQuery } from '../../../utils/nestBaseQuery';
import type { components, operations } from '../../../api/types/api';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

/** Request/response types from OpenAPI (api.ts) */
type ExtractExcelRequestDto = components['schemas']['ExtractExcelRequestDto'];
type ExtractExcelResponseDto =
  operations['ImportsController_extractItemExcel']['responses'][200]['content']['application/json'];

/** One row from Excel extract or draft load (labels / mixed types). */
export type ExtractExcelItemRow = components['schemas']['ItemExtractResponseDto']['itemData'][number];

/** One row for POST /imports/items/insert (numeric IDs and rates). */
export type ItemImportItemRow = components['schemas']['ItemImportRequestDto']['itemData'][number];

export type ImportItemsRequestDto = components['schemas']['ItemImportRequestDto'];
export type ImportItemsResponseDto = components['schemas']['ItemImportResponseDto'];
export type SaveItemsDraftRequestDto = components['schemas']['SaveImportDraftRequestDto'];
export type SaveItemsDraftResponseDto =
  operations['ImportsController_saveDraft']['responses'][200]['content']['application/json'];
export type GetItemsDraftResponseDto =
  operations['ImportsController_getItemDraft']['responses'][200]['content']['application/json'];
export type DeleteDraftResponseDto =
  operations['ImportsController_deleteDraft']['responses'][200]['content']['application/json'];

// -----------------------------------------------------------------------------
// API definition & export
// -----------------------------------------------------------------------------

export const itemImportApi = createApi({
  reducerPath: 'itemImportApi',
  baseQuery: nestBaseQuery,
  tagTypes: ['ItemImportExtract'],
  endpoints: (builder) => ({
    extractExcel: builder.mutation<ExtractExcelResponseDto, ExtractExcelRequestDto>({
      query: (arg) => client.POST('/api/v1/imports/excel/extract/items', { body: arg }),
      invalidatesTags: ['ItemImportExtract'],
    }),
    /** Bulk insert parsed item rows (Nest: POST /api/v1/imports/items/insert). */
    importItems: builder.mutation<ImportItemsResponseDto, ImportItemsRequestDto>({
      query: (arg) => {
        return client.POST('/api/v1/imports/items/insert', {
          body: arg
        });
      },
    }),
    saveItemsDraft: builder.mutation<SaveItemsDraftResponseDto, SaveItemsDraftRequestDto>({
      query: (arg) =>
        client.POST('/api/v1/imports/drafts/save', {
          body: arg,
        }),
    }),
    getItemsDraft: builder.query<GetItemsDraftResponseDto, void>({
      query: () => client.GET('/api/v1/imports/drafts/items'),
    }),
    deleteItemsDraft: builder.mutation<DeleteDraftResponseDto, void>({
      query: () =>
        client.DELETE('/api/v1/imports/drafts/{importType}', {
          params: { path: { importType: 'ITEM' } },
        }),
    }),
  }),
});

export const {
  useExtractExcelMutation,
  useImportItemsMutation,
  useSaveItemsDraftMutation,
  useLazyGetItemsDraftQuery,
  useDeleteItemsDraftMutation,
} = itemImportApi;
